// ============================================================================
// 建物データ取得 / 国土地理院「地理院地図Vector」実験公開タイル(試験実装)
// ----------------------------------------------------------------------------
// OSM由来の建物データ(buildingTiles.ts)は地域によって網羅率にムラがあり、
// 実際に住宅が密集しているのにほぼ描画されない地域があることが判明した
// (実例: 江戸川区花火大会周辺、鹿骨二丁目でOSM登録12件 vs 本データ139件)。
// 国土地理院が実験公開しているベクトルタイル(ログイン不要・MVT形式)を
// 都度ライブ取得する代替案。app.tsの「建物データに国土地理院(試験提供)を
// 使う」トグルで切り替えられる(既定はOFF=OSM)。
//
// 高さ属性がない点について: OSM側も同じ地域(江戸川区周辺8,307棟)を実測した
// ところ、height/building:levelsタグを持つのはわずか1.5%(128棟)で、残り
// 98.5%は元々このアプリの既定値9mに頼っていた。つまり本データに切り替えても
// 高さ精度の後退は1.5%分にとどまり、網羅率の改善(鹿骨二丁目で12倍以上)の方が
// 実用上大きい。ただし全国・全ズームでの網羅率は未検証。
//
// 出典表示が必要（利用規約）: 「国土地理院ベクトルタイル提供実験」。
// 参照: https://github.com/gsi-cyberjapan/gsimaps-vector-experiment
// ============================================================================

import { VectorTile } from "@mapbox/vector-tile";
import { PbfReader } from "pbf";
import { distanceMeters } from "./geo";
import type { Building, LngLat } from "./types";
import {
  localToLngLat,
  ringArea,
  ringIntersectsBbox,
  tileBboxDistance,
  tilesForBbox,
} from "./pmtilesCommon";

const TILE_URL_TEMPLATE = "https://cyberjapandata.gsi.go.jp/xyz/experimental_bvmap/{z}/{x}/{y}.pbf";

// 実験提供のズーム範囲は4〜16(17はズーム16のオーバーズーム表示)。z13は1タイル
// 約4km四方で、打ち上げ地点近傍(数〜十数km)を数十枚のライブ取得で賄える実用的な
// バランス（z16まで下げると1タイル約500mになり、同じ範囲で千枚単位になってしまう）。
const TILE_ZOOM = 13;

// buildingTiles.tsと同じ安全弁。このデータソースは高さ属性を持たないため、
// 既定の建物高さ(9m、buildingTiles.tsのparseHeight未タグ時のフォールバックと同じ)
// で代用する。
const MAX_DECODED_FEATURES = 300000;
const DEFAULT_HEIGHT_M = 9;

async function fetchTile(z: number, x: number, y: number): Promise<ArrayBuffer | null> {
  const url = TILE_URL_TEMPLATE.replace("{z}", String(z)).replace("{x}", String(x)).replace(
    "{y}",
    String(y)
  );
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`GSI tile fetch ${res.status}: ${url}`);
  return res.arrayBuffer();
}

/**
 * bbox [south, west, north, east] にかかる国土地理院ベクトルタイルをライブ取得し、
 * buildingレイヤーをBuilding[]に変換する(試験実装。高さは既定値9mで代用)。
 */
export async function fetchGsiBuildingsFromTiles(
  south: number,
  west: number,
  north: number,
  east: number
): Promise<Building[]> {
  const center: LngLat = [(west + east) / 2, (south + north) / 2];
  const tileCoords = tilesForBbox(south, west, north, east, TILE_ZOOM).sort(
    (a, b) =>
      tileBboxDistance(center, a.tx, a.ty, TILE_ZOOM) - tileBboxDistance(center, b.tx, b.ty, TILE_ZOOM)
  );

  const tileBytes = await Promise.all(tileCoords.map(({ tx, ty }) => fetchTile(TILE_ZOOM, tx, ty)));

  const candidates: (Building & { dist: number })[] = [];
  let nextId = 1;
  for (let t = 0; t < tileCoords.length; t++) {
    if (candidates.length >= MAX_DECODED_FEATURES) break;
    const bytes = tileBytes[t];
    if (!bytes) continue;
    const { tx, ty } = tileCoords[t];
    const tile = new VectorTile(new PbfReader(new Uint8Array(bytes)));
    const layer = tile.layers.building;
    if (!layer) continue;
    for (let i = 0; i < layer.length; i++) {
      const f = layer.feature(i);
      const rings = f
        .loadGeometry()
        .map((pts) => pts.map((pt): LngLat => localToLngLat(pt.x, pt.y, tx, ty, TILE_ZOOM, f.extent)))
        .filter((ring) => ring.length >= 3);
      if (rings.length === 0) continue;
      const outer = rings.reduce((a, b) => (ringArea(b) > ringArea(a) ? b : a));
      if (!ringIntersectsBbox(outer, south, west, north, east)) continue;
      candidates.push({
        id: nextId++,
        ring: outer,
        height: DEFAULT_HEIGHT_M,
        estimated: true,
        dist: distanceMeters(center, outer[0]),
      });
    }
  }
  candidates.sort((a, b) => a.dist - b.dist);
  return candidates.slice(0, MAX_DECODED_FEATURES).map(({ dist: _dist, ...b }) => b);
}
